import {Editor, Element, Transforms, type BlockNode} from "slate";

import {toggleList, unwrapList} from "./list-helpers.ts";

export const getParentBlock = (editor: Editor) => {
    const [parent] = Editor.nodes<BlockNode>(editor, {
        match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            Editor.isBlock(editor, n),
    });
    return parent;
};

export const getParentBlockForSuggestions = (editor: Editor) => {
    const [firstParentEntry, ...others] = Editor.nodes<BlockNode>(editor, {
        at: editor.selection!,
        mode: "highest",
        match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            [
                "heading-one",
                "heading-two",
                "heading-three",
                "list-item",
                "paragraph",
                "block-quote",
                "code-block",
                "table-cell",
            ].includes(n.type),
    });
    if (!firstParentEntry) {
        return null;
    }

    if (others.length > 0) {
        // If there are other blocks returned, it means that the selection
        // spans multiple blocks. We don't want to show suggestions in this
        // case, since requesting edits will likely break the document layout,
        // so we just bail out here.
        return null;
    }

    return firstParentEntry[0];
};

/**
 * Checks for an active block (non-inline element) at the current selection.
 */
export const isBlockActive = (
    editor: Editor,
    blockType: BlockNode["type"],
): boolean => {
    const {selection} = editor;
    if (!selection) {
        return false;
    }

    const [match] = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, selection),
            match: (n) =>
                !Editor.isEditor(n) &&
                Element.isElement(n) &&
                n.type === blockType,
        }),
    );

    return !!match;
};

/**
 * Sets block (non-inline element) formatting at the current selection.
 * For example, this can turn a paragraph into a heading-one, or vice versa.
 * If the selection is within a list, the selected list elements will be
 * unwrapped completely and turned into the new block type.
 */
export const setBlockType = (editor: Editor, newType: BlockNode["type"]) => {
    switch (newType) {
        case "bulleted-list":
        case "numbered-list":
        case "lettered-list": {
            toggleList(editor, newType);
            return;
        }

        default: {
            unwrapList(editor);
            Transforms.setNodes(editor, {type: newType});
        }
    }
};

/**
 * Returns the active block type at the current selection. Used to determine
 * if the cursor/selection is within a paragraph, heading, blockquote, etc.
 */
export const getBlockType = (editor: Editor): BlockNode["type"] => {
    const [match] = Array.from(
        Editor.nodes<BlockNode>(editor, {
            mode: "lowest",
            match: (n) =>
                !Editor.isEditor(n) &&
                Element.isElement(n) &&
                Editor.isBlock(editor, n) &&
                n.type !== "list-item" &&
                n.type !== "list-item-text",
        }),
    );

    if (match) {
        return match[0].type;
    }

    return "paragraph";
};
